<template>

<div class="base-timer">
    <svg class="base-timer__svg" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <g class="base-timer__circle">
            <circle class="base-timer__path-elapsed" cx="50" cy="50" r="45" />
            <path
            id="base-timer-path-remaining"
            :stroke-dasharray="circleDashArray"
            class="base-timer__path-remaining"
            :class="remainingPathColor"
            d="
            M 50, 50
            m -45, 0
            a 45,45 0 1,0 90,0
            a 45,45 0 1,0 -90,0
            "
            ></path>
        </g>
    </svg>
    <span id="base-timer-label" class="base-timer__label">
        {{ formatTime }}
    </span>
</div>

</template>

<script>
// Credit: Mateusz Rybczonec
const FULL_DASH_ARRAY = 283;
export default {
    name: 'timer',
    props: {
        startTime: {
            type: String,
            required: true
        },
        timeLimit: {
            type: Number,
            required: true
        },
        openLanes: {
            type: [Boolean, Number, String],
            required: false
        },
        warningThreshold: {
            type: Number,
            default: 900
        },
        alertThreshold: {
            type: Number,
            default: 300
        },
        debug: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            timeLimitSeconds: 0,
            timeLeft: 0,
            timePassed: 0,
            timerInterval: null,
            startTimeMoment: null
        }
    },
    mounted() {
        this.timeLeft = this.timeLimitSeconds
        this.startTimeMoment = this.$moment(this.startTime, 'HHmm')
        if (this.openLanes) {
            this.timeLimitSeconds = 0
            this.startOpenLaneTimer()
        } else {
            this.timeLimitSeconds = this.timeLimit * 60
            this.startTimer()
        }
    },
    computed: {
        formatTime() {
            let timeLeft = this.$moment.duration(this.timeLeft, "seconds")
            // The largest round integer less than or equal to the result of time divided being by 60.
            // const minutes = Math.floor(this.timeLeft / 60);

            // Seconds are the remainder of the time divided by 60 (modulus operator)
            let seconds = (timeLeft.seconds() < 0) ? "00" : timeLeft.seconds().toString().padStart(2, "0")
            let minutes = (timeLeft.minutes() < 0) ? "00" : timeLeft.minutes().toString().padStart(2, "0")
            let hours = (timeLeft.hours() < 0) ? "00" : timeLeft.hours().toString().padStart(2, "0")
            // If the value of seconds is less than 10, then display seconds with a leading zero
            // if (seconds < 10) {
            //     seconds = `0${seconds}`;
            // }


            // The output in MM:SS format
            return `${hours}:${minutes}:${seconds}`;
        },
        remainingPathColor() {
            const COLOR_CODES = {
                info: {
                    color: "green"
                },
                warning: {
                    color: "orange",
                    threshold: this.warningThreshold
                },
                alert: {
                    color: "red",
                    threshold: this.alertThreshold
                }
            }
            if (this.timeLeft <= this.alertThreshold) {
                return COLOR_CODES.alert.color
            } else if (this.timeLeft <= this.warningThreshold) {
                return COLOR_CODES.warning.color
            }
            return COLOR_CODES.info.color
        },
        calculateTimeFraction() {
            const rawTimeFraction = this.timeLeft / this.timeLimitSeconds;
            return rawTimeFraction - (1 / this.timeLimitSeconds) * (1 - rawTimeFraction);
        },
        circleDashArray() {
            if (this.debug)
                console.log(this.calculateTimeFraction * FULL_DASH_ARRAY);
            let circleDash = (this.calculateTimeFraction * FULL_DASH_ARRAY <= 0.2 ? FULL_DASH_ARRAY : this.calculateTimeFraction * FULL_DASH_ARRAY)
            return `${(
                circleDash
            ).toFixed(0)} 283`
        }
    },
    methods: {
        startTimer() {
            this.timerInterval = setInterval(() => {
                if (this.startTimeMoment.isSameOrBefore(this.$moment())) {
                    // The amount of time passed increments by one
                    this.timePassed = this.$moment().diff(this.startTimeMoment, "seconds");
                    this.timeLeft = this.timeLimitSeconds - this.timePassed;

                    if (this.timeLeft === 0) {
                        clearInterval(this.timerInterval)
                    }
                }
            }, 1000);
        },
        startOpenLaneTimer() {
            this.timerInterval = setInterval(() => {
                if (this.startTimeMoment.isSameOrBefore(this.$moment())) {
                    // The amount of time passed increments by one
                    this.timePassed = this.$moment().diff(this.startTimeMoment, "seconds");
                    this.timeLeft = this.timeLimitSeconds + this.timePassed;
                    // if (this.timeLeft === 0) {
                    //     clearInterval(this.timerInterval)
                    // }
                }
            }, 1000);
        }
    }
}
</script>

<style lang="scss" scoped>
$circleWidth: 160px;
$circleHeight: 160px;

/* Sets the containers height and width */
.base-timer {
    position: relative;
    height: $circleWidth;
    width: $circleHeight;
}

/* Removes SVG styling that would hide the time label */
.base-timer__circle {
    fill: none;
    stroke: none;
}

/* The SVG path that displays the timer's progress */
.base-timer__path-elapsed {
    stroke-width: 7px;
    stroke: grey;
}

.base-timer__label {
    position: absolute;

    /* Size should match the parent container */
    width: $circleWidth;
    height: $circleHeight;

    /* Keep the label aligned to the top */
    top: 0;

    /* Create a flexible box that centers content vertically and horizontally */
    display: flex;
    align-items: center;
    justify-content: center;

    /* Sort of an arbitrary number; adjust to your liking */
    font-size: 32px;
}
.base-timer__path-remaining {
    /* Just as thick as the original ring */
    stroke-width: 7px;

    /* Rounds the line endings to create a seamless circle */
    stroke-linecap: round;

    /* Makes sure the animation starts at the top of the circle */
    transform: rotate(90deg);
    transform-origin: center;

    /* One second aligns with the speed of the countdown timer */
    transition: 1s linear all;

    /* Allows the ring to change color when the color value updates */
    stroke: currentColor;

    &.green {
        color: rgb(65, 184, 131);
    }

    &.orange {
        color: orange;
    }

    &.red {
        color: red;
    }
}

.base-timer__svg {
    /* Flips the svg and makes the animation to move left-to-right */
    transform: scaleX(-1);
}
</style>
